import type { AxiosInstance } from 'axios';
import { z } from 'zod';

// Zod schemas for type safety
const PerformanceMetricsSchema = z.object({
  wins: z.number(),
  losses: z.number(),
  pushes: z.number(),
  winRate: z.number(),
  roi: z.number(),
  avgExpectedValue: z.number(),
  edge: z.number(),
  performanceRatio: z.number(),
  expectedProfit: z.number(),
  actualProfit: z.number(),
  avgBreakEvenRate: z.number()
});

const StrengthMetricsSchema = z.object({
  count: z.number(),
  avgExpectedValue: z.number(),
  edge: z.number(),
  winRate: z.number(),
  performanceRatio: z.number(),
  roi: z.number()
});

const PerformanceByStrengthSchema = z.object({
  strong: StrengthMetricsSchema.optional(),
  normal: StrengthMetricsSchema.optional(),
  weak: StrengthMetricsSchema.optional(),
  no_bet: z.object({
    count: z.number(),
    expectedValue: z.number().default(0),
    winRate: z.number().default(0),
    roi: z.number().default(0)
  }).optional()
});

const WindowSchema = z.object({
  start: z.string(),
  end: z.string(),
  pickCount: z.number(),
  settledCount: z.number()
});

const DataQualitySchema = z.object({
  completenessRate: z.number(),
  warnings: z.array(z.string())
});

const PerformanceDataSchema = z.object({
  id: z.string(),
  date: z.string(),
  metricType: z.enum(['r100', 'r500', 'd30', 'd60', 'd90', 'all', 'season']),
  outcomeId: z.string(),
  versionId: z.string(),
  scope: z.enum(['version', 'model']).optional(),
  performance: PerformanceMetricsSchema,
  byStrength: PerformanceByStrengthSchema,
  window: WindowSchema,
  dataQuality: DataQualitySchema,
  created: z.string(),
  lastUpdated: z.string(),
  typeId: z.string()
});

// Leaderboard schemas
const LeaderboardEntrySchema = z.object({
  id: z.string(),
  versionId: z.string(),
  rank: z.number(),
  date: z.string(),
  metricType: z.string(),
  domainId: z.string().optional(),
  outcomeId: z.string().optional(),
  sampleSize: z.number(),
  modelName: z.string().optional(),
  modelPicture: z.string().optional(),
  modelDescription: z.string().optional(),
  labName: z.string().optional(),
  performance: PerformanceMetricsSchema
});

const LeaderboardResultSchema = z.object({
  id: z.string(),
  typeId: z.string(),
  metricType: z.string(),
  domainId: z.string().optional(),
  outcomeId: z.string().optional(),
  created: z.string(),
  lastUpdated: z.string(),
  generatedAt: z.string(),
  ttl: z.number().optional(),
  items: z.array(LeaderboardEntrySchema)
});

// TypeScript types derived from schemas
export type PerformanceMetrics = z.infer<typeof PerformanceMetricsSchema>;
export type StrengthMetrics = z.infer<typeof StrengthMetricsSchema>;
export type PerformanceByStrength = z.infer<typeof PerformanceByStrengthSchema>;
export type PerformanceData = z.infer<typeof PerformanceDataSchema>;
export type MetricType = 'r100' | 'r500' | 'd30' | 'd60' | 'd90' | 'all' | 'season';
export type PerformanceScope = 'version' | 'model';
export type LeaderboardEntry = z.infer<typeof LeaderboardEntrySchema>;
export type LeaderboardResult = z.infer<typeof LeaderboardResultSchema>;

// Service factory function
export function PerformanceService(api: AxiosInstance) {

  /**
   * Get model performance data
   * @param modelId - The model ID
   * @param metricType - The metric type (d30, r100, etc.)
   * @param fromDate - Start date for data range
   * @param toDate - End date for data range
   * @param scope - 'version' for current version only (default), 'model' for all versions
   * @returns Array of performance data
   */
  async function getModelPerformance(
    modelId: string,
    metricType: MetricType = 'd30',
    fromDate?: Date,
    toDate?: Date,
    scope: PerformanceScope = 'version'
  ): Promise<PerformanceData[]> {
    // Default to last 14 days if dates not provided
    const now = new Date();
    const defaultFromDate = new Date(now);
    defaultFromDate.setDate(defaultFromDate.getDate() - 14);

    const response = await api.get(
      `/models/${modelId}/performance`,
      {
        params: {
          fromDate: (fromDate || defaultFromDate).toISOString().split('T')[0],
          toDate: (toDate || now).toISOString().split('T')[0],
          metricType,
          scope
        }
      }
    );

    // Validate and parse response
    const results = z.array(PerformanceDataSchema).parse(response.data);

    // Filter out no_bet data as requested
    return results.map(item => ({
      ...item,
      byStrength: {
        strong: item.byStrength.strong,
        normal: item.byStrength.normal,
        weak: item.byStrength.weak
        // Exclude no_bet
      }
    }));
  }

  /**
   * Get the latest performance snapshot for a model
   * @param modelId - The model ID
   * @param metricType - The metric type (d30, r100, etc.)
   * @returns Latest performance data or null if not available
   */
  async function getLatestPerformance(
    modelId: string,
    metricType: MetricType = 'd30'
  ): Promise<PerformanceData | null> {
    const now = new Date();
    const performances = await getModelPerformance(modelId, metricType, now, now);

    if (performances.length > 0) {
      // Return the most recent entry
      return performances.sort((a, b) =>
        new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
      )[0];
    }

    return null;
  }

  /**
   * Get performance leaderboard
   * @param metricType - The metric type (d30, all, r100, r500, d60, d90, season)
   * @param domainId - Optional domain filter (e.g., 'nfl')
   * @param outcomeId - Optional outcome filter (e.g., 'moneyline')
   * @returns Leaderboard result with ranked models
   */
  async function getLeaderboard(
    metricType: MetricType = 'd30',
    domainId?: string,
    outcomeId?: string
  ): Promise<LeaderboardResult> {
    const params: Record<string, string> = {
      metricType
    };

    if (domainId) {
      params.domainId = domainId;
    }
    if (outcomeId) {
      params.outcomeId = outcomeId;
    }

    const response = await api.get('/leaderboard/performance', { params });

    // Validate and parse response
    return LeaderboardResultSchema.parse(response.data);
  }

  return {
    getModelPerformance,
    getLatestPerformance,
    getLeaderboard
  };
}